import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOSchema from '@/components/feature/SEOSchema';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    setFormStatus('submitting');
    setFormError('');
    try {
      const formData = new FormData(form);
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'company_alt') {
          body.append(key, value as string);
        }
      });
      const res = await fetch('https://readdy.ai/api/form/d9otjk2ftmtnfcfh0dug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed?.meta as { message?: string })?.message || (parsed?.message as string) || responseText || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const faqData = [
    { q: '韩漫和日漫有什么区别？', a: '韩漫（韩国漫画）主要采用竖屏下拉式阅读方式，针对手机屏幕进行优化。与日漫的黑白风格不同，绝大多数韩漫都是全彩绘制，画风更加精致华丽。在题材上，韩漫同样丰富多样，涵盖奇幻、恋爱、悬疑、武侠等多种类型。' },
    { q: '本站的漫画资源是免费的吗？', a: '是的！韩漫漫画网站上的所有内容均可免费阅读。我们采用下拉式阅读方式，无需翻页，高清画质，无广告干扰，为您提供最纯粹的阅读体验。' },
    { q: '如何找到我想看的韩漫？', a: '您可以通过首页的分类浏览功能，按奇幻冒险、恋爱校园、剧情悬疑、武侠古风等类型筛选作品。也可以使用排行榜功能查看当前最热门的韩漫。如果您有具体的作品名称，可以使用搜索功能直接查找。' },
    { q: '漫画更新频率是怎样的？', a: '本站的连载漫画保持与韩国官方平台同步更新，大多数热门作品每周更新一次。您可以在"最近更新"板块查看最新上线的章节。完结作品一次性提供全部章节，方便您一口气读完。' },
    { q: '可以在手机上看吗？', a: '当然可以！韩漫天然适合手机阅读。本站采用响应式设计，完美适配手机、平板和电脑等各种设备。打开浏览器访问我们的网站，即可随时随地畅享韩漫阅读。' },
    { q: '如何联系我们？', a: '您可以通过本页面的联系表单向我们发送消息，或直接发送邮件至 contact@hanmancomic.com。我们会在24小时内回复您的咨询。' },
  ];

  return (
    <>
      <SEOSchema type="Organization" />
      <SEOSchema
        type="WebPage"
        data={{
          name: '关于我们 - 韩漫漫画网站',
          description: '了解韩漫漫画网站的品牌故事、用户口碑和联系方式。我们致力于提供完整韩漫大全、优质推荐和流行排行榜。',
          url: 'https://www.hanmancomic.com/about',
        }}
      />
      <SEOSchema
        type="FAQPage"
        data={{
          mainEntity: faqData.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.a,
            },
          })),
        }}
      />

      <Navbar />

      <main className="min-h-screen pt-20 pb-14">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="mt-8 mb-16">
            <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-6">关于我们</h1>
            <div className="relative aspect-[21/9] rounded-lg overflow-hidden bg-background-100 mb-8">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20cozy%20reading%20environment%20with%20bookshelves%20and%20comfortable%20seating%2C%20soft%20natural%20lighting%2C%20artistic%20Korean%20comic%20illustrations%20on%20walls%2C%20modern%20minimal%20interior%20design%2C%20peaceful%20atmosphere%2C%20warm%20wood%20tones%2C%20inviting%20creative%20space&width=1600&height=600&seq=about-hero-comics&orientation=landscape"
                alt="韩漫漫画网站"
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="space-y-5 text-sm md:text-base text-foreground-700 leading-relaxed">
              <p>
                <strong className="text-foreground-900">韩漫漫画网站</strong>成立于2024年，是一个专注于韩国漫画的中文内容平台。我们的使命是让每一位华语读者都能轻松接触到高质量的韩国漫画，享受纯粹、无广告的阅读体验。
              </p>
              <p>
                我们深知漫画爱好者的需求——高清画质、流畅阅读、及时更新。因此，我们的网站采用了先进的竖屏下拉式阅读技术，所有漫画均为高清全彩画质，无需翻页，只需轻轻下滑即可连续阅读。无论是在手机上还是电脑上，都能获得极佳的阅读体验。
              </p>
              <p>
                我们的编辑团队由资深的韩漫爱好者组成，每天精心筛选和推荐最值得阅读的作品。从热门的奇幻冒险到甜蜜的校园恋爱，从扣人心弦的悬疑剧情到刀光剑影的武侠江湖，我们涵盖了韩漫的所有主流类型，确保每一位读者都能找到自己喜欢的作品。
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8 text-center">我们的优势</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { icon: 'ri-shield-check-line', title: '高清正版', desc: '全彩高清画质，最优阅读体验' },
                { icon: 'ri-flashlight-line', title: '极速更新', desc: '与韩国平台同步，第一时间追更' },
                { icon: 'ri-emotion-happy-line', title: '无广告', desc: '纯粹阅读，没有任何干扰元素' },
              ].map((item) => (
                <div key={item.title} className="text-center p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 mb-4">
                    <i className={`${item.icon} text-accent-600 text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8 text-center">用户口碑</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { name: '小明', role: '韩漫资深读者', text: '这个网站真的太棒了！画质清晰，更新速度超快，而且完全没有广告，阅读体验一流。已经推荐给身边所有喜欢韩漫的朋友了！', rating: 5 },
                { name: '花卷', role: '漫画博主', text: '作为一个经常需要找韩漫素材的博主，这个网站的分类和排行榜功能帮助我节省了大量时间。推荐的作品质量都很高！', rating: 5 },
                { name: '追漫达人阿杰', role: '大学生', text: '手机上阅读非常流畅，下拉式的设计太方便了。最近追的几部连载更新也很快，基本和韩国同步，强烈推荐！', rating: 5 },
                { name: '小雨', role: '设计师', text: '网站的设计很简洁大方，使用起来很舒服。漫画的画质也很高，在iPad上全屏观看效果特别好。', rating: 4 },
                { name: '漫画迷老王', role: '上班族', text: '通勤路上看韩漫已经成为日常了。这个网站资源很全，想看的漫画基本都能找到，而且免费无广告，真的太良心了。', rating: 5 },
                { name: '星光', role: '高中生', text: '同学推荐来的，一下子就爱上了。分类很清晰，找漫画很方便。最喜欢排行榜功能，帮我发现了很多好作品。', rating: 5 },
              ].map((review) => (
                <div key={review.name} className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-2 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{review.text}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-label text-primary-700">{review.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-xs font-label font-medium text-foreground-900">{review.name}</p>
                      <p className="text-[10px] text-foreground-500">{review.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8 text-center">常见问题</h2>
            <div className="space-y-3">
              {faqData.map((faq, index) => (
                <details key={index} className="group rounded-lg bg-background-100 border border-background-200/70">
                  <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer list-none">
                    <span className="text-sm font-label font-medium text-foreground-900 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8 text-center">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-mail-line text-accent-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-label font-medium text-foreground-900 mb-1">电子邮件</h3>
                      <p className="text-xs text-foreground-500">contact@hanmancomic.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-time-line text-accent-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-label font-medium text-foreground-900 mb-1">工作时间</h3>
                      <p className="text-xs text-foreground-500">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-chat-smile-2-line text-accent-600"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-label font-medium text-foreground-900 mb-1">社交媒体</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="微信">
                          <i className="ri-wechat-line text-foreground-600 text-sm"></i>
                        </a>
                        <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="微博">
                          <i className="ri-weibo-line text-foreground-600 text-sm"></i>
                        </a>
                        <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="B站">
                          <i className="ri-bilibili-line text-foreground-600 text-sm"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <form
                  data-readdy-form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label htmlFor="name" className="block text-xs font-label text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-label text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-xs font-label text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入消息主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-xs font-label text-foreground-700 mb-1.5">消息内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的消息（最多500字）"
                    ></textarea>
                    <p className="text-[10px] text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-foreground-300 text-background-50 dark:text-foreground-950 rounded-lg font-label text-sm transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                  </button>

                  {formStatus === 'success' && (
                    <p className="text-sm text-center text-primary-600 font-label">消息已发送成功！我们会尽快回复您。</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-center text-foreground-600 font-label">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}